import {PrimerComponentFaqGroupSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentFaqGroup'
import {PrimerComponentSubnavSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSubnav'
import {defineBrandPage, optionalSection, optionalTemplateField} from '../../../brand/components/BrandPage'
import {
  findByContentType,
  findByIdWithSchema,
  GenericContentSchema,
  GenericGroupSchema,
  GenericSectionSchema,
  type GenericSection,
} from '../../../brand/lib/types/contentful'
import {getPlanItems} from './content-plan-items'

export const featuresCopilotPage = defineBrandPage({
  subnav: optionalTemplateField('subnav', PrimerComponentSubnavSchema),
  hero: optionalSection('heroSection', GenericSectionSchema, toHeroSectionContent),
  features: optionalSection('featuresSection', GenericSectionSchema, toFeaturesSectionContent),
  models: optionalSection('modelsSection', GenericSectionSchema, toModelsSectionContent),
  setup: optionalSection('setupSection', GenericSectionSchema, toSetupSectionContent),
  resources: optionalSection('resourcesSection', GenericSectionSchema, toResourcesSectionContent),
  pricing: optionalSection('pricingSection', GenericSectionSchema, toPricingSectionContent),
  platforms: optionalSection('platformsSection', GenericSectionSchema, toPlatformsSectionContent),
  cta: optionalSection('ctaSection', GenericSectionSchema, toCtaSectionContent),
  faq: optionalTemplateField('faq', PrimerComponentFaqGroupSchema),
})

export function toHeroSectionContent(heroSection: GenericSection) {
  const content = heroSection.fields.content

  return {
    section: heroSection,
    intro: findByIdWithSchema(content, 'heroIntro', GenericContentSchema),
    tabs: findByIdWithSchema(content, 'heroTabs', GenericGroupSchema),
    logoSuite: findByIdWithSchema(content, 'logoSuite', GenericContentSchema),
  }
}

export function toFeaturesSectionContent(featuresSection: GenericSection) {
  const content = featuresSection.fields.content

  return {
    section: featuresSection,
    sectionIntro: featuresSection.fields.sectionIntro,
    riverAccordion: findByContentType(content, 'primerComponentRiverAccordion'),
    cards: findByContentType(content, 'primerCards'),
  }
}

export function toModelsSectionContent(modelsSection: GenericSection) {
  const content = modelsSection.fields.content

  return {
    section: modelsSection,
    sectionIntro: modelsSection.fields.sectionIntro,
    cards: findByContentType(content, 'primerCards'),
    visual: findByIdWithSchema(content, 'modelsVisual', GenericContentSchema),
  }
}

export function toSetupSectionContent(setupSection: GenericSection) {
  return {
    section: setupSection,
    sectionIntro: setupSection.fields.sectionIntro,
    cards: findByContentType(setupSection.fields.content, 'primerCards'),
  }
}

export function toResourcesSectionContent(resourcesSection: GenericSection) {
  const content = resourcesSection.fields.content

  return {
    section: resourcesSection,
    sectionIntro: resourcesSection.fields.sectionIntro,
    cards: findByContentType(content, 'primerCards'),
    divider: findByIdWithSchema(content, 'resourcesDivider', GenericContentSchema),
  }
}

export function toPricingSectionContent(pricingSection: GenericSection) {
  const content = pricingSection.fields.content
  const planTypes = findByIdWithSchema(content, 'featuresCopilotPlanTypes', GenericContentSchema)

  return {
    section: pricingSection,
    sectionIntro: pricingSection.fields.sectionIntro,
    planTypes,
    planItems: getPlanItems(planTypes),
    pricingOptions: findByIdWithSchema(content, 'featuresCopilotPricingOptions', GenericGroupSchema),
    pricingFooter: findByIdWithSchema(content, 'featuresCopilotPricingFooter', GenericContentSchema),
  }
}

export function toPlatformsSectionContent(platformsSection: GenericSection) {
  const content = platformsSection.fields.content

  return {
    section: platformsSection,
    heading: findByIdWithSchema(content, 'platformsHeading', GenericContentSchema),
  }
}

export function toCtaSectionContent(ctaSection: GenericSection) {
  const content = ctaSection.fields.content

  return {
    section: ctaSection,
    ctaBanner: findByContentType(content, 'primerComponentCtaBanner'),
  }
}

export type HeroSectionContent = ReturnType<typeof toHeroSectionContent>
export type FeaturesSectionContent = ReturnType<typeof toFeaturesSectionContent>
export type ModelsSectionContent = ReturnType<typeof toModelsSectionContent>
export type SetupSectionContent = ReturnType<typeof toSetupSectionContent>
export type ResourcesSectionContent = ReturnType<typeof toResourcesSectionContent>
export type PricingSectionContent = ReturnType<typeof toPricingSectionContent>
export type PlatformsSectionContent = ReturnType<typeof toPlatformsSectionContent>
export type CtaSectionContent = ReturnType<typeof toCtaSectionContent>
